import { useState, useRef, useCallback } from 'react'
import { notifyMediaChange } from '@/lib/mediaEffects';
import { useQueryClient } from '@tanstack/react-query'
import { MediaFolder, mediaApi } from '@wordpuppi/shared'
import { MediaBrowser } from './MediaBrowser'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { alertDialog } from '@/lib/dialog'
import { pickFilesDesktop } from '@/utils/desktopActions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface ImageInsertDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  siteSlug: string
  folder: MediaFolder
  onInsert: (url: string, alt?: string, mimeType?: string) => void
  /** AB#662: also offer documents (pdf/word/excel/markdown/txt/csv) — callers
   *  that insert an <img> (e.g. the feature image) leave this off. */
  allowDocuments?: boolean
}

// AB#662: document types the editor can link to, mirrored from the media page.
const DOCUMENT_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.md,.txt,.csv'

export function ImageInsertDialog({
  open,
  onOpenChange,
  siteSlug,
  folder,
  onInsert,
  allowDocuments = false,
}: ImageInsertDialogProps) {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'browse' | 'upload' | 'url'>('browse')
  const [url, setUrl] = useState('')
  const [alt, setAlt] = useState('')
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleClose = () => {
    setUrl('')
    setAlt('')
    setActiveTab('browse')
    onOpenChange(false)
  }

  const handleBrowseSelect = (selectedUrl: string, selectedAlt?: string, mimeType?: string) => {
    onInsert(selectedUrl, selectedAlt, mimeType)
    handleClose()
  }

  const handleUrlInsert = () => {
    if (url.trim()) {
      onInsert(url.trim(), alt.trim() || undefined)
      handleClose()
    }
  }

  const uploadFiles = useCallback(async (files: File[]) => {
    const file = files[0]
    if (!file) return

    setUploading(true)
    try {
      const result = await mediaApi.upload(siteSlug, file, folder)
      void notifyMediaChange(queryClient, siteSlug, result.data ? [result.data] : [], 'added').catch(() => {})
      onInsert(result.data!.url, file.name, result.data!.mime_type || file.type)
      handleClose()
    } catch (err) {
      console.error('Upload failed:', err)
      await alertDialog('Upload failed. Please try again.')
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [siteSlug, folder, onInsert])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    uploadFiles(Array.from(e.target.files ?? []))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{allowDocuments ? 'Insert Media' : 'Insert Image'}</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList className="w-full">
            <TabsTrigger value="browse" className="flex-1">
              Browse Media
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex-1">
              Upload
            </TabsTrigger>
            <TabsTrigger value="url" className="flex-1">
              URL
            </TabsTrigger>
          </TabsList>

          <TabsContent value="browse" className="mt-4">
            <MediaBrowser
              siteSlug={siteSlug}
              currentFolder={folder}
              onSelect={handleBrowseSelect}
              onCancel={handleClose}
              allowDocuments={allowDocuments}
            />
          </TabsContent>

          <TabsContent value="upload" className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label>{allowDocuments ? 'Choose an image or document' : 'Choose an image file'}</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept={allowDocuments ? DOCUMENT_ACCEPT : 'image/*'}
                onClick={(e) => pickFilesDesktop(e, { multiple: false, accept: allowDocuments ? DOCUMENT_ACCEPT : 'image/*' }, uploadFiles)}
                onChange={handleFileChange}
                disabled={uploading}
              />
              {uploading && (
                <p className="text-sm text-muted-foreground">Uploading...</p>
              )}
            </div>
            <div className="flex justify-end">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="url" className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="image-url">{allowDocuments ? 'File URL' : 'Image URL'}</Label>
              <Input
                id="image-url"
                type="url"
                placeholder="https://example.com/image.jpg"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="image-alt">Alt text (optional)</Label>
              <Input
                id="image-alt"
                type="text"
                placeholder="Describe the image"
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="button" onClick={handleUrlInsert} disabled={!url.trim()}>
                Insert
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
